import CustomField from '../form/CustomField'
import styles from './styles.module.scss'
import { useState, useRef, useEffect, useCallback } from 'react'
import Button from '../Button'
import { withModifiers } from '../../helpers/withModifiers'
import Scroll from 'react-scroll'
import { useResponsive } from '../../../providers/ResponsiveContext'

const DropdownField = ({
  variant = [],
  children,
  isActive,
  shadowRef,
  onClear,
  onApply,
  angle = true,
  footer = true,
  disableClear,
  disableApply,
  scrollTop = false,
  clearTranslation,
  applyTranslation,
  triggerClose,
  defaultOpen = false,
  ...props
}) => {
  const { isLaptop } = useResponsive()
  const wrapperRef = useRef(null)
  const contentRef = useRef(null)

  const [show, setShow] = useState(defaultOpen)
  const [contentVisible, setContentVisible] = useState(defaultOpen)
  const isSimple = variant.length !== 0 && variant.includes('content-simple')
  const scroll = Scroll.animateScroll

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (!defaultOpen) {
        if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
          setShow(false)
        }
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Handle triggerClose prop changes
  useEffect(() => {
    triggerClose && setShow(false)
  }, [triggerClose])

  // Handle shadow effect when dropdown is visible
  useEffect(() => {
    if (shadowRef && !defaultOpen) {
      if (show) {
        shadowRef.current?.classList.add('isVisible')
      } else {
        shadowRef.current?.classList.remove('isVisible')
      }
    }
  }, [shadowRef, show])

  // Handle content visibility for render optimization
  useEffect(() => {
    if (show) {
      // Make content visible immediately when showing
      setContentVisible(true)
    } else {
      // Add delay to hide content after transition completes
      const timer = setTimeout(() => {
        setContentVisible(false)
      }, 300) // Adjust timing to match your CSS transition duration
      return () => clearTimeout(timer)
    }
  }, [show])

  // Handle clear action
  const handleClear = useCallback(() => {
    onClear && onClear()
    setShow(false)
    if (scrollTop) {
      scroll.scrollToTop()
    }
  }, [onClear, scrollTop, scroll])

  // Handle apply action
  const handleApply = useCallback(() => {
    onApply && onApply()
    setShow(false)
    if (scrollTop) {
      scroll.scrollToTop()
    }
  }, [onApply, scrollTop, scroll])

  return (
    <div
      ref={wrapperRef}
      className={withModifiers('field', variant, styles)}
      role="group"
      aria-labelledby="formik-group"
    >
      {isSimple ? (
        !isLaptop && <div className={styles.field__label}>{props.value}</div>
      ) : (
        <CustomField
          variant={variant}
          type="text"
          readOnly
          angle={angle}
          isActive={isActive || show}
          onClick={() => setShow(!show)}
          {...props}
        />
      )}

      <div
        ref={contentRef}
        className={withModifiers(
          'field__content',
          [show && 'is-active'],
          styles
        )}
        aria-hidden={!show}
      >
        {(contentVisible || show) && (
          <div className={styles.field__wrapper}>
            {isSimple && isLaptop && (
              <div className={styles.field__label}>{props.value}</div>
            )}

            {children}

            {footer && !isSimple && (
              <div className={styles.field__footer}>
                <Button
                  onClick={handleClear}
                  disabled={disableClear}
                  arrow={false}
                  variant={['xs', 'secondary', '-offset-left']}
                  type="button"
                >
                  {clearTranslation}
                </Button>

                {onApply && (
                  <Button
                    variant={['xs']}
                    arrow={false}
                    onClick={handleApply}
                    type="submit"
                    disabled={disableApply}
                    className={styles.footer}
                  >
                    {applyTranslation}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default DropdownField
